// src/pages/HomeScreen.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/AppContext';
import { t } from '../lib/i18n';
import HeroSlider from '../components/shared/HeroSlider';
import { useSEO, websiteSchema, organizationSchema, collectionPageSchema } from '../lib/useSEO';
import NewsletterSignup from '../components/shared/NewsletterSignup';

function ProductCard({ product }) {
  const { lang, fmt, addToCart } = useApp();
  const [added, setAdded] = useState(false);
  const navigate = useNavigate();

  const handleAdd = (e) => {
    e.stopPropagation();
    if (product.stock < 1) return;
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  const name = product.name?.[lang] || product.name?.en || 'Product';
  const hasOldPrice = product.price_eur_original && product.price_eur_original > product.price_eur;

  return (
    <div className="prod-card" onClick={() => navigate(`/product/${product.id}`)}>
      <div className="prod-card-img">
        {product.images?.[0]
          ? <img src={product.images[0]} alt={name} loading="lazy" />
          : <span style={{ opacity: .2 }}>🌿</span>
        }
        {product.badge && (
          <span className={`prod-card-badge badge-${product.badge}`}>
            {product.badge === 'hot'  ? t('bestseller', lang)
           : product.badge === 'new'  ? t('new_badge',  lang)
           : product.badge === 'sale' ? t('sale',       lang)
           : product.badge}
          </span>
        )}
        {product.stock < 1 && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: '.8rem', fontWeight: 700,
              letterSpacing: '.12em', textTransform: 'uppercase', color: '#666',
            }}>
              {t('out_of_stock', lang)}
            </span>
          </div>
        )}
      </div>
      <div className="prod-card-body">
        <div className="prod-card-cat">
          {product.category?.name?.[lang] || product.category?.name?.en || ''}
        </div>
        <div className="prod-card-name">{name}</div>
        {Array.isArray(product.tags) && product.tags.length > 0 && (
          <div className="prod-card-tags">
            {product.tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="prod-tag">{tag}</span>
            ))}
          </div>
        )}
        <div className="prod-card-foot">
          <div>
            <span className="prod-price">{fmt(product.price_eur)}</span>
            {hasOldPrice && (
              <span className="prod-price-old">{fmt(product.price_eur_original)}</span>
            )}
          </div>
          <button
            className={`prod-add-btn ${added ? 'added' : ''}`}
            onClick={handleAdd}
            disabled={product.stock < 1}
            aria-label={t('add_to_cart', lang)}
          >
            {added ? '✓' : '+'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductSection({ id, eyebrow, title, products, lang }) {
  if (!products?.length) return null;
  return (
    <div id={`section-${id}`}>
      <div className="section-header">
        <div>
          <span className="section-eyebrow">{eyebrow}</span>
          <div className="section-title">{title}</div>
        </div>
      </div>
      <div className="prod-grid">
        {products.map(p => <ProductCard key={p.id} product={p} />)}
      </div>
      <div className="section-divider" />
    </div>
  );
}

export default function HomeScreen() {
  const { lang, searchQuery, dispatch } = useApp();
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const mainRef = useRef(null);

  useSEO({
    // Home gets the bare site name as title (no suffix)
    title:       null,
    description: 'Premium CBD botanical collector specimens delivered discreetly across Europe. CBD flowers, hemp resin, vapes & collector bundles. 18+ EU-compliant.',
    canonical:   '/',
    lang,
    jsonLd:      [websiteSchema(), organizationSchema(), collectionPageSchema(
      'Premium Botanical Collectibles',
      'CBD flowers, hemp resin, vapes and collector bundles. EU-compliant, discreet shipping.',
      '/',
    )],
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('products')
      .select('*, category:categories(slug, name, icon)')
      .eq('active', true)
      .order('sort_order');

    if (error) { console.error(error); setLoading(false); return; }

    setSections({
      'new-arrivals': data.filter(p => p.is_new || p.featured),
      'cbd-flower':   data.filter(p => p.category?.slug === 'cbd-flower'),
      'hemp-resin':   data.filter(p => p.category?.slug === 'hemp-resin'),
      'vapes-carts':  data.filter(p => p.category?.slug === 'vapes-carts'),
      'bundles':      data.filter(p => p.category?.slug === 'bundles'),
    });
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Track scroll to update active category
  useEffect(() => {
    const cats = ['new-arrivals', 'cbd-flower', 'hemp-resin', 'vapes-carts', 'bundles'];
    const offset = 88 + 50 + 48; // topnav (88px) + catnav + section header breathing room
    const handleScroll = () => {
      for (let i = cats.length - 1; i >= 0; i--) {
        const el = document.getElementById(`section-${cats[i]}`);
        if (el && el.getBoundingClientRect().top <= offset) {
          dispatch({ type: 'SET_ACTIVE_CATEGORY', payload: cats[i] });
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [dispatch]);

  const filterProducts = (products) => {
    if (!searchQuery) return products;
    const q = searchQuery.toLowerCase();
    return products?.filter(p => {
      const name = (p.name?.[lang] || p.name?.en || '').toLowerCase();
      const tags = (p.tags || []).join(' ').toLowerCase();
      return name.includes(q) || tags.includes(q);
    });
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );

  const noResults = searchQuery && Object.values(sections).every(s => !filterProducts(s)?.length);

  return (
    <main ref={mainRef}>
      {/* Hero Slideshow */}
      <HeroSlider />

      {/* Legal notice bar */}
      <div className="legal-bar">
        <p>⚠️ {t('collector_notice', lang)}</p>
      </div>

      {/* All product sections */}
      <div className="home-products-wrapper" style={{ padding: '0 0 48px', background: 'var(--bg)' }}>
        <div className="page-container">
          <ProductSection
            id="new-arrivals"
            eyebrow="Just dropped"
            title={t('new_arrivals', lang)}
            products={filterProducts(sections['new-arrivals'])}
            lang={lang}
          />
          <ProductSection
            id="cbd-flower"
            eyebrow="Botanical collector specimens"
            title={t('cbd_flower', lang)}
            products={filterProducts(sections['cbd-flower'])}
            lang={lang}
          />
          <ProductSection
            id="hemp-resin"
            eyebrow="Artisan collectibles"
            title={t('hemp_resin', lang)}
            products={filterProducts(sections['hemp-resin'])}
            lang={lang}
          />
          <ProductSection
            id="vapes-carts"
            eyebrow="Hardware & cartridges"
            title={t('vapes_carts', lang)}
            products={filterProducts(sections['vapes-carts'])}
            lang={lang}
          />
          <ProductSection
            id="bundles"
            eyebrow="Best value"
            title={t('bundles', lang)}
            products={filterProducts(sections['bundles'])}
            lang={lang}
          />

          {noResults && (
            <div style={{ textAlign: 'center', padding: '6rem 0', color: '#555' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: .2 }}>🔍</div>
              <p style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                letterSpacing: '.1em', textTransform: 'uppercase', fontSize: '.9rem',
              }}>
                No products found for "{searchQuery}"
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Newsletter section — only show when not searching */}
      {!searchQuery && <NewsletterSignup variant="section" source="homepage" />}
    </main>
  );
}